/**
 * Labstep
 *
 * @module grid/SmartTableProtocolCondition/components/ToolPanel/AddColumn
 * @desc Wizard to add a column
 */

import { EntitySearchMulti } from 'labstep-web/components/Entity/Search/Multi';
import type {
  EntitySearchMultiStaticLists,
  IEntitySearchMultiList,
} from 'labstep-web/components/Entity/Search/Multi/types';
import { ParamsStateContextProvider } from 'labstep-web/contexts/params-state';
import { ActionComponent } from 'labstep-web/core/Action/Component';
import { Flex } from 'labstep-web/core/Flex';
import { Header } from 'labstep-web/core/Header';
import { useToolPanelContext } from 'labstep-web/grid/Index/components/ToolPanel/context';
import GridSmartTableTemplateLoader from 'labstep-web/grid/SmartTableProtocolCondition/components/TemplateLoader';
import { ToolPanelState } from 'labstep-web/grid/SmartTableProtocolCondition/components/ToolPanel';
import { getDeviceLists } from 'labstep-web/grid/SmartTableProtocolCondition/components/ToolPanel/AddColumn/Device';
import { getOrderRequestLists } from 'labstep-web/grid/SmartTableProtocolCondition/components/ToolPanel/AddColumn/OrderRequest';
import { getResourceLists } from 'labstep-web/grid/SmartTableProtocolCondition/components/ToolPanel/AddColumn/Resource';
import { getResourceItemLists } from 'labstep-web/grid/SmartTableProtocolCondition/components/ToolPanel/AddColumn/ResourceItem';
import { getProtocolConditionList } from 'labstep-web/grid/SmartTableProtocolCondition/components/ToolPanel/InventoryFields';
import type { IndexTemplatableEntity } from 'labstep-web/models';
import { Device } from 'labstep-web/models/device.model';
import type { EntityView } from 'labstep-web/models/entity-view.model';
import type { Experiment } from 'labstep-web/models/experiment.model';
import type { Metadata } from 'labstep-web/models/metadata';
import { OrderRequest } from 'labstep-web/models/order-request.model';
import type { Protocol } from 'labstep-web/models/protocol.model';
import { ProtocolCondition } from 'labstep-web/models/protocol-condition.model';
import type { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import { Resource } from 'labstep-web/models/resource.model';
import { ResourceItem } from 'labstep-web/models/resource-item.model';
import type React from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { IEntityDataGridToolPanelAddColumnProps } from './types';

export const getStaticLists = (
  entityView: EntityView,
  selectMetadata: (
    metadata: Metadata,
    parentEntityClass: string,
  ) => void,
  colIds: string[],
  setProtocolValue: Dispatch<SetStateAction<ProtocolValue | null>>,
  experimentOrProtocol: Experiment | Protocol,
): EntitySearchMultiStaticLists => {
  if (entityView.entity_name === ProtocolCondition.entityName) {
    return getProtocolConditionList(
      entityView,
      experimentOrProtocol,
      setProtocolValue,
      selectMetadata,
      colIds,
    );
  }
  return () => <div></div>;
};

export const getLists = (
  entityView: EntityView,
  selectMetadata: (
    metadata: Metadata,
    parentEntityClass: string,
  ) => void,
  colIds: string[],
  template?: IndexTemplatableEntity,
): IEntitySearchMultiList[] => {
  if (entityView.entity_name === Device.entityName) {
    return getDeviceLists(
      entityView,
      selectMetadata,
      colIds,
      template,
    );
  }
  if (entityView.entity_name === OrderRequest.entityName) {
    return getOrderRequestLists(entityView, selectMetadata, colIds);
  }
  if (entityView.entity_name === Resource.entityName) {
    return getResourceLists(
      entityView,
      selectMetadata,
      colIds,
      template,
    );
  }
  if (entityView.entity_name === ResourceItem.entityName) {
    return getResourceItemLists(
      entityView,
      selectMetadata,
      colIds,
      template,
    );
  }
  return [];
};

export const EntityDataGridToolPanelAddColumn: React.FC<
  IEntityDataGridToolPanelAddColumnProps
> = ({
  colIds,
  entityView,
  selectMetadata,
  setProtocolValue,
  experimentOrProtocol,
}) => {
  const toolPanelContext = useToolPanelContext();
  return (
    <GridSmartTableTemplateLoader
      entityName={entityView.entity_name}
      templateId={entityView.templateId}
    >
      {({ template }: { template?: IndexTemplatableEntity }) => {
        return (
          <div data-cy="grid/SmartTableProtocolCondition/components/ToolPanel/AddColumn">
            <Flex vAlignContent="center" spacing="gap" shrink={0}>
              <ActionComponent
                type="icon"
                icon="arrow left"
                text="Go Back"
                onClick={() =>
                  toolPanelContext?.setToolPanelState(
                    ToolPanelState.default,
                  )
                }
              />
              <Header size="tiny" noMargin>
                Add Column
              </Header>
            </Flex>
            <small>
              First you must select the field that you wish to add as
              a column
            </small>
            <ParamsStateContextProvider value={{}}>
              <EntitySearchMulti
                staticLists={getStaticLists(
                  entityView,
                  selectMetadata,
                  colIds,
                  setProtocolValue,
                  experimentOrProtocol,
                )}
                lists={getLists(
                  entityView,
                  selectMetadata,
                  colIds,
                  template,
                )}
              />
            </ParamsStateContextProvider>
          </div>
        );
      }}
    </GridSmartTableTemplateLoader>
  );
};
